import Image from "next/image";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const Main = styled.main`
  padding-top: 8rem;
  padding-bottom: 4rem;
  background: linear-gradient(to bottom, rgba(173, 216, 230, 0.1), white);

  @media (min-width: 768px) {
    padding-top: 9rem;
    padding-bottom: 6rem;
  }
`;

export const Container = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  animation: ${fadeIn} 0.6s ease-out;

  @media (min-width: 640px) {
    max-width: 640px;
  }

  @media (min-width: 768px) {
    max-width: 768px;
  }

  @media (min-width: 1024px) {
    max-width: 1024px;
  }

  @media (min-width: 1280px) {
    max-width: 1280px;
  }

  @media (min-width: 1536px) {
    max-width: 1536px;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  align-items: center;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const TextContainer = styled.div`
  max-width: 40rem;
`;

export const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: #e0f2fe;
  color: ${({ theme }) => theme.colors.primary[3]};
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
`;

export const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primaryText[9]};
  line-height: 1.2;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    font-size: 3rem;
  }
  @media (min-width: 1024px) {
    font-size: 3.75rem;
  }
`;

export const Description = styled.p`
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.primaryText[9]};
  margin-bottom: 2rem;
  animation: fade-in 0.5s ease-in-out 0.3s;
`;

export const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
  animation: fade-in 0.5s ease-in-out 0.45s;

  a {
    text-decoration: none;
  }

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

export const StyledButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 9999px;
  transition: background 0.3s ease;

  &.primary {
    background-color: ${({ theme }) => theme.colors.primary[3]};
    border: none;
    color: ${({ theme }) => theme.colors.primaryText.white};
    &:hover {
      background-color: ${({ theme }) => theme.colors.primary[4]};
    }
  }

  &.outline {
    border: 1px solid #e5e7eb;
    color: ${({ theme }) => theme.colors.primaryText[8]};
    background: transparent;
    &:hover {
      background-color: #e5e7eb;
    }
  }
`;

export const Features = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  animation: fade-in 0.5s ease-in-out 0.6s;

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

export const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const FeatureIcon = styled.div`
  background: ${({ theme }) => theme.colors.primary[1]};
  border-radius: 999px;
  padding: 0.25rem;
`;

export const FeatureText = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.primary[3]};
  background: #e0f2fe;
  padding: 0.25rem;
  width: 24px;
  height: 24px;
  border-radius: 50%;
`;

export const ImageWrapper = styled.div`
  position: relative;
  animation: fade-in 1s ease-in-out;
`;

export const ImageBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom right,
    rgba(0, 123, 255, 0.1),
    rgba(32, 201, 151, 0.1)
  );
  border-radius: 1rem;
  transform: rotate(3deg) scale(0.98);
`;

export const ImageContainer = styled.div`
  position: relative;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

export const StyledImage = styled(Image)`
  width: 100%;
  height: auto;
  object-fit: cover;
`;
